import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter, Play, CheckCircle, XCircle, Lightbulb, BookOpen, FileText, Download, Monitor, Wand2 } from 'lucide-react';
import MathRenderer from '../components/MathRenderer';
import TestGenerator from '../components/TestGenerator';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function DeepPractice() {
  const { API, getHeaders, profile } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({ class_level: profile?.class_level || 6, chapter: '', difficulty: '', question_type: '', limit: 10 });
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [answer, setAnswer] = useState('');
  const [answerSteps, setAnswerSteps] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [mode, setMode] = useState('practice'); // practice | worksheet_online | worksheet_pdf | custom_test
  const [worksheetAnswers, setWorksheetAnswers] = useState({});
  const [worksheetSteps, setWorksheetSteps] = useState({});
  const [wsSubmitted, setWsSubmitted] = useState(false);
  const [wsResult, setWsResult] = useState(null);
  const worksheetRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/config/all`).then(r => r.json()).then(setConfig).catch(() => {});
  }, [API]);

  const chapters = config?.config_chapters?.filter(c => c.class_key === String(filters.class_level)) || [];
  const difficulties = config?.config_difficulty_levels || [];
  const questionTypes = config?.config_question_types || [];

  const startPractice = async (practiceMode) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (practiceMode === 'worksheet_pdf' || practiceMode === 'worksheet_online') params.set('limit', '20');
      const res = await fetch(`${API}/practice/deep/questions?${params}`, { headers: getHeaders() });
      const data = await res.json();
      if (!data.questions?.length) { toast.error('No questions found for these filters'); setLoading(false); return; }

      if (practiceMode === 'worksheet_pdf') {
        setQuestions(data.questions);
        setMode('worksheet_pdf');
        setShowFilters(false);
        setTimeout(() => downloadPDF(), 500);
      } else if (practiceMode === 'worksheet_online') {
        setQuestions(data.questions);
        setMode('worksheet_online');
        setShowFilters(false);
        setWorksheetAnswers({});
        setWorksheetSteps({});
        setWsSubmitted(false);
        setWsResult(null);
      } else {
        const sRes = await fetch(`${API}/practice/deep/start`, {
          method: 'POST', headers: getHeaders(),
          body: JSON.stringify({ filters, question_ids: data.questions.map(q => q.id) }),
        });
        const sData = await sRes.json();
        setSessionId(sData.session?.id);
        setQuestions(data.questions);
        setCurrentIdx(0);
        setScore({ correct: 0, total: 0 });
        setAnswer('');
        setAnswerSteps('');
        setFeedback(null);
        setMode('practice');
        setShowFilters(false);
        setStartTime(Date.now());
      }
    } catch { toast.error('Failed to start'); }
    finally { setLoading(false); }
  };

  const submitAnswer = async () => {
    if (!answer && questions[currentIdx]?.question_type !== 'true_false') return;
    const q = questions[currentIdx];
    const timeTaken = Math.round((Date.now() - (startTime || Date.now())) / 1000);
    try {
      const res = await fetch(`${API}/practice/deep/answer`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ session_id: sessionId, question_id: q.id, answer, time_taken_seconds: timeTaken, steps: answerSteps }),
      });
      const data = await res.json();
      setFeedback(data);
      setScore(s => ({ correct: s.correct + (data.is_correct ? 1 : 0), total: s.total + 1 }));
    } catch { toast.error('Failed to submit'); }
  };

  const nextQuestion = () => {
    if (currentIdx >= questions.length - 1) {
      fetch(`${API}/practice/deep/complete`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ session_id: sessionId, time_taken_seconds: Math.round((Date.now() - (startTime || Date.now())) / 1000) }),
      });
      toast.success(`Practice complete! Score: ${score.correct}/${score.total}`);
      navigate('/home');
      return;
    }
    setCurrentIdx(i => i + 1);
    setAnswer('');
    setAnswerSteps('');
    setFeedback(null);
    setStartTime(Date.now());
  };

  const downloadPDF = async () => {
    const el = worksheetRef.current;
    if (!el) return;
    try {
      toast.info('Generating PDF...');
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      let yOffset = 0;
      const pageH = pdf.internal.pageSize.getHeight();
      while (yOffset < h) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -yOffset, w, h);
        yOffset += pageH;
      }
      pdf.save(`MathTopper_Worksheet_Class${filters.class_level}.pdf`);
      toast.success('PDF downloaded!');
    } catch { toast.error('PDF generation failed'); }
  };

  const submitWorksheet = async () => {
    let correct = 0;
    const total = questions.length;
    for (const q of questions) {
      const ans = worksheetAnswers[q.id] || '';
      const qType = q.question_type || 'mcq';
      if (qType === 'mcq' || qType === 'assertion_reason') {
        if (ans.toUpperCase() === (q.correct_option || '').toUpperCase()) correct++;
      } else if (qType === 'true_false') {
        if (ans.toLowerCase() === (q.correct_answer || '').toLowerCase()) correct++;
      } else if (['fill_blank', 'integer_type'].includes(qType)) {
        if (String(ans).trim().toLowerCase() === String(q.correct_answer || '').trim().toLowerCase()) correct++;
      } else if (qType === 'subjective') {
        // Subjective needs review; mark as pending
      }
    }
    const pct = Math.round((correct / total) * 100);
    setWsResult({ correct, total, score_percent: pct });
    setWsSubmitted(true);
    toast.success(`Worksheet complete! ${correct}/${total}`);
  };

  const q = questions[currentIdx];

  // ─── Render Answer Input based on question type ───
  const renderAnswerInput = (q, value, onChange, stepsValue, onStepsChange, prefix = '') => {
    const qType = q.question_type || 'mcq';

    if (qType === 'mcq' || qType === 'assertion_reason') {
      return (
        <div className="space-y-2">
          {['A','B','C','D'].map(opt => {
            const text = q[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            return (
              <button key={opt} data-testid={`${prefix}option-${opt}`} onClick={() => onChange(opt)}
                className={`w-full text-left p-3 rounded-xl border-2 font-bold text-sm transition-all
                  ${value === opt ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE] hover:border-[#1B4F8A]'}`}>
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F0E6D8] text-[#1B4F8A] text-xs font-extrabold mr-2">{opt}</span>
                <MathRenderer text={text} />
              </button>
            );
          })}
        </div>
      );
    }
    if (qType === 'multi_select') {
      return (
        <div className="space-y-2">
          {['A','B','C','D'].map(opt => {
            const text = q[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            const sel = (value || '').split(',').filter(Boolean);
            const isSel = sel.includes(opt);
            return (
              <button key={opt} onClick={() => {
                const newSel = isSel ? sel.filter(s => s !== opt) : [...sel, opt];
                onChange(newSel.join(','));
              }} className={`w-full text-left p-3 rounded-xl border-2 font-bold text-sm transition-all ${isSel ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>
                {isSel ? '☑' : '☐'} <MathRenderer text={text} />
              </button>
            );
          })}
        </div>
      );
    }
    if (qType === 'true_false') {
      return (
        <div className="flex gap-3">
          {['true','false'].map(v => (
            <button key={v} data-testid={`${prefix}tf-${v}`} onClick={() => onChange(v)}
              className={`flex-1 p-3 rounded-xl border-2 font-bold text-sm capitalize ${value === v ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>{v}</button>
          ))}
        </div>
      );
    }
    if (qType === 'subjective') {
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-slate-500 mb-1 block">Your Answer</label>
            <input data-testid={`${prefix}subjective-answer`} value={value || ''} onChange={e => onChange(e.target.value)}
              placeholder="Final answer" className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 mb-1 block">Working / Steps (show your work)</label>
            <textarea data-testid={`${prefix}subjective-steps`} rows={4} value={stepsValue || ''} onChange={e => onStepsChange(e.target.value)}
              placeholder={"Step 1: Identify what's given\nStep 2: Apply the formula\nStep 3: Calculate\nStep 4: Verify"}
              className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none resize-y" />
          </div>
        </div>
      );
    }
    if (qType === 'match' || qType === 'matrix_match') {
      return (
        <div>
          {q.match_pairs && (() => { try { return JSON.parse(q.match_pairs); } catch { return []; } })().map((p, i) => (
            <div key={i} className="flex gap-2 text-sm mb-1"><span className="font-bold">{i+1}. {p.left}</span> <span className="text-slate-400">→</span> <span>{p.right}</span></div>
          ))}
          <input data-testid={`${prefix}match-input`} value={value || ''} onChange={e => onChange(e.target.value)}
            placeholder="e.g. 1-A,2-B,3-C,4-D" className="w-full mt-2 rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
        </div>
      );
    }
    // Default: text input (fill_blank, integer_type, ordering, passage_based, diagram_based)
    return (
      <input data-testid={`${prefix}answer-input`} type={qType === 'integer_type' ? 'number' : 'text'}
        value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Type your answer"
        className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
    );
  };

  return (
    <div className="min-h-screen pb-6" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => showFilters ? navigate('/home') : setShowFilters(true)} data-testid="deep-practice-back-btn"><ArrowLeft className="w-5 h-5 text-[#1B4F8A]" /></button>
        <BookOpen className="w-5 h-5 text-[#FF6B35]" />
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">
          {mode === 'worksheet_online' ? 'Worksheet' : mode === 'worksheet_pdf' ? 'PDF Preview' : mode === 'custom_test' ? 'Create Your Test' : 'Deep Practice'}
        </h1>
        {sessionId && <span className="ml-auto text-sm font-bold text-[#FF6B35]">{score.correct}/{score.total}</span>}
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-4">
        {/* Filters */}
        {showFilters && (
          <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-[#1B4F8A]" />
                <h2 className="font-bold text-[#1B4F8A]">Choose Your Practice</h2>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block">Class</label>
                  <select data-testid="deep-class-filter" value={filters.class_level} onChange={e => setFilters({...filters, class_level: parseInt(e.target.value), chapter: ''})} className="w-full rounded-xl border-2 border-[#E5DACE] px-3 py-2.5 text-sm font-bold bg-white">
                    {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 mb-1 block">Chapter</label>
                  <select data-testid="deep-chapter-filter" value={filters.chapter} onChange={e => setFilters({...filters, chapter: e.target.value})} className="w-full rounded-xl border-2 border-[#E5DACE] px-3 py-2.5 text-sm font-bold bg-white">
                    <option value="">All Chapters</option>
                    {chapters.map(ch => <option key={ch.key} value={ch.key}>{ch.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block">Difficulty</label>
                    <select data-testid="deep-difficulty-filter" value={filters.difficulty} onChange={e => setFilters({...filters, difficulty: e.target.value})} className="w-full rounded-xl border-2 border-[#E5DACE] px-3 py-2.5 text-sm font-bold bg-white">
                      <option value="">All</option>
                      {difficulties.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 mb-1 block">Type</label>
                    <select data-testid="deep-type-filter" value={filters.question_type} onChange={e => setFilters({...filters, question_type: e.target.value})} className="w-full rounded-xl border-2 border-[#E5DACE] px-3 py-2.5 text-sm font-bold bg-white">
                      <option value="">All</option>
                      {questionTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Mode Buttons */}
                <div className="grid grid-cols-1 gap-2 pt-2">
                  <Button data-testid="start-deep-practice-btn" onClick={() => startPractice('practice')} disabled={loading}
                    className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-extrabold rounded-full py-3 shadow-[0_4px_0_#C04918] active:shadow-none active:translate-y-[4px]">
                    <Play className="w-4 h-4 mr-2" /> {loading ? 'Loading...' : 'Start Practice (1 at a time)'}
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="start-worksheet-online-btn" onClick={() => startPractice('worksheet_online')} disabled={loading}
                      className="bg-[#1B4F8A] hover:bg-[#143D6E] text-white font-bold rounded-full py-2.5 text-sm">
                      <Monitor className="w-4 h-4 mr-1" /> Attempt Online
                    </Button>
                    <Button data-testid="start-worksheet-pdf-btn" onClick={() => startPractice('worksheet_pdf')} disabled={loading}
                      className="bg-[#8338EC] hover:bg-[#6A2DC4] text-white font-bold rounded-full py-2.5 text-sm">
                      <Download className="w-4 h-4 mr-1" /> Download PDF
                    </Button>
                  </div>
                  <Button data-testid="create-custom-test-btn" onClick={() => { setShowFilters(false); setMode('custom_test'); }}
                    className="w-full bg-white border-2 border-[#8338EC] text-[#8338EC] hover:bg-[#8338EC] hover:text-white font-bold rounded-full py-2.5 text-sm mt-2">
                    <Wand2 className="w-4 h-4 mr-1" /> Create Your Own Test Paper
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── Custom Test Generator Mode ─── */}
        {mode === 'custom_test' && (
          <TestGenerator classLevel={filters.class_level} mode="student"
            onSave={(questions, title, totalMarks) => {
              setQuestions(questions);
              setMode('worksheet_online');
              setShowFilters(false);
              toast.success(`Test paper ready! ${questions.length} questions, ${totalMarks} marks`);
            }}
          />
        )}

        {/* ─── Practice Mode (1 question at a time) ─── */}
        {mode === 'practice' && q && !showFilters && (
          <>
            <div className="mb-4">
              <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                <span>Question {currentIdx + 1} of {questions.length}</span>
                <span className="capitalize text-[#FF6B35]">{q.difficulty} | {q.question_type}</span>
              </div>
              <div className="h-2 bg-[#F0E6D8] rounded-full overflow-hidden">
                <div className="h-full bg-[#FF6B35] rounded-full transition-all" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }} />
              </div>
            </div>

            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-5">
                <p className="text-xs font-bold text-slate-400 mb-2 uppercase">{q.chapter?.replace(/_/g, ' ')} | {q.marks}m</p>
                <div className="text-base font-bold text-[#1B4F8A] mb-4 leading-relaxed">
                  <MathRenderer text={q.question_latex || q.question_text} />
                </div>
                {!feedback && renderAnswerInput(q, answer, setAnswer, answerSteps, setAnswerSteps)}
              </CardContent>
            </Card>

            {feedback && (
              <Card className={`border-2 rounded-2xl mb-4 ${feedback.is_correct ? 'border-[#06D6A0] bg-[#06D6A0]/5' : feedback.is_correct === null ? 'border-[#FFD23F] bg-[#FFD23F]/5' : 'border-red-300 bg-red-50/50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {feedback.is_correct === null ? (
                      <><Lightbulb className="w-5 h-5 text-[#FFD23F]" /><span className="font-bold text-sm text-[#FFD23F]">Submitted for review</span></>
                    ) : feedback.is_correct ? (
                      <><CheckCircle className="w-5 h-5 text-[#06D6A0]" /><span className="font-bold text-sm text-[#06D6A0]">Correct! +{feedback.coins_earned} coins</span></>
                    ) : (
                      <><XCircle className="w-5 h-5 text-red-500" /><span className="font-bold text-sm text-red-500">Incorrect. Answer: {feedback.correct_answer}</span></>
                    )}
                  </div>
                  {feedback.solution && (
                    <div className="text-sm text-slate-600 mt-2 whitespace-pre-line">
                      <p className="font-bold text-[#1B4F8A] text-xs mb-1">Solution:</p>
                      <MathRenderer text={feedback.solution_latex || feedback.solution} />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {!feedback ? (
              <Button data-testid="submit-answer-btn" onClick={submitAnswer} disabled={!answer && q.question_type !== 'subjective'}
                className="w-full bg-[#1B4F8A] hover:bg-[#143D6E] text-white font-extrabold rounded-full py-3 shadow-[0_4px_0_#0D2E52] active:shadow-none active:translate-y-[4px]">
                Check Answer
              </Button>
            ) : (
              <Button data-testid="next-question-btn" onClick={nextQuestion}
                className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-extrabold rounded-full py-3 shadow-[0_4px_0_#C04918] active:shadow-none active:translate-y-[4px]">
                {currentIdx >= questions.length - 1 ? 'Finish Practice' : 'Next Question'}
              </Button>
            )}
          </>
        )}

        {/* ─── Worksheet Online Mode ─── */}
        {mode === 'worksheet_online' && !showFilters && !wsSubmitted && (
          <>
            <p className="text-sm font-bold text-[#1B4F8A] mb-3">Answer all {questions.length} questions, then submit</p>
            <div className="space-y-4">
              {questions.map((wq, idx) => (
                <Card key={wq.id} className="border-2 border-[#E5DACE] rounded-2xl">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-7 h-7 rounded-full bg-[#1B4F8A] text-white text-xs font-extrabold flex items-center justify-center">{idx + 1}</span>
                      <span className="text-xs font-bold text-[#FF6B35] capitalize">{wq.question_type} | {wq.marks}m</span>
                      <span className="text-xs text-slate-400 ml-auto capitalize">{wq.difficulty}</span>
                    </div>
                    <div className="text-sm font-bold text-[#1B4F8A] mb-3 leading-relaxed">
                      <MathRenderer text={wq.question_latex || wq.question_text} />
                    </div>
                    {renderAnswerInput(
                      wq,
                      worksheetAnswers[wq.id] || '',
                      val => setWorksheetAnswers(prev => ({...prev, [wq.id]: val})),
                      worksheetSteps[wq.id] || '',
                      val => setWorksheetSteps(prev => ({...prev, [wq.id]: val})),
                      `ws-${idx}-`
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button data-testid="submit-worksheet-btn" onClick={submitWorksheet}
              className="w-full mt-4 bg-[#FF6B35] hover:bg-[#E85D2A] text-white font-extrabold rounded-full py-3 shadow-[0_4px_0_#C04918]">
              Submit Worksheet
            </Button>
          </>
        )}

        {/* Worksheet results */}
        {mode === 'worksheet_online' && wsSubmitted && wsResult && (
          <Card className="border-2 border-[#06D6A0] rounded-2xl">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-[#06D6A0] mx-auto mb-3" />
              <p className="text-2xl font-black text-[#1B4F8A] mb-1">{wsResult.score_percent}%</p>
              <p className="text-sm font-bold text-slate-500">{wsResult.correct}/{wsResult.total} correct</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button onClick={() => setShowFilters(true)} className="bg-[#1B4F8A] text-white font-bold rounded-full px-6">New Worksheet</Button>
                <Button onClick={() => navigate('/home')} variant="outline" className="font-bold rounded-full px-6 border-2 border-[#E5DACE]">Home</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ─── PDF Preview / Worksheet for Download ─── */}
        {mode === 'worksheet_pdf' && !showFilters && (
          <>
            <div className="flex gap-2 mb-4">
              <Button data-testid="download-pdf-btn" onClick={downloadPDF} className="bg-[#8338EC] text-white font-bold rounded-full px-4">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button onClick={() => setShowFilters(true)} variant="outline" className="font-bold rounded-full border-2 border-[#E5DACE]">Back</Button>
            </div>
            <div ref={worksheetRef} className="bg-white rounded-2xl p-6 shadow-sm" style={{ minWidth: 600 }}>
              <div className="text-center mb-6 border-b-2 border-[#E5DACE] pb-4">
                <h2 className="text-xl font-extrabold text-[#1B4F8A]">MathTopper Worksheet</h2>
                <p className="text-sm text-slate-500">Class {filters.class_level} | {filters.chapter?.replace(/_/g, ' ') || 'Mixed'} | {filters.difficulty || 'All Levels'}</p>
                <p className="text-xs text-slate-400 mt-1">Total Questions: {questions.length} | Date: {new Date().toLocaleDateString()}</p>
              </div>
              <div className="space-y-6">
                {questions.map((wq, idx) => (
                  <div key={wq.id} className="border-b border-dashed border-[#E5DACE] pb-4">
                    <div className="flex gap-2 items-start">
                      <span className="font-extrabold text-[#1B4F8A] text-sm shrink-0">Q{idx + 1}.</span>
                      <div className="flex-1">
                        <span className="text-xs font-bold text-[#FF6B35] capitalize">[{wq.question_type}] [{wq.marks}m]</span>
                        <p className="text-sm font-bold text-[#1B4F8A] mt-1">{wq.question_latex || wq.question_text}</p>
                        {(wq.question_type === 'mcq' || wq.question_type === 'assertion_reason' || wq.question_type === 'multi_select') && (
                          <div className="mt-2 grid grid-cols-2 gap-1 text-sm">
                            {['A','B','C','D'].map(opt => {
                              const t = wq[`option_${opt.toLowerCase()}`];
                              return t ? <p key={opt}><span className="font-bold">{opt})</span> {t}</p> : null;
                            })}
                          </div>
                        )}
                        {wq.question_type === 'match' && wq.match_pairs && (
                          <div className="mt-2 text-sm">
                            {(() => { try { return JSON.parse(wq.match_pairs); } catch { return []; } })().map((p, i) => (
                              <p key={i}>{i+1}. {p.left} → ___</p>
                            ))}
                          </div>
                        )}
                        {wq.question_type === 'subjective' && (
                          <div className="mt-2">
                            <p className="text-xs text-slate-500">Answer:</p>
                            <div className="border-b border-dotted border-slate-300 h-8 mt-1" />
                            <p className="text-xs text-slate-500 mt-2">Working:</p>
                            {[1,2,3,4].map(i => <div key={i} className="border-b border-dotted border-slate-300 h-6 mt-1" />)}
                          </div>
                        )}
                        {['fill_blank', 'integer_type'].includes(wq.question_type) && (
                          <div className="mt-2"><p className="text-xs text-slate-500">Answer: ___________</p></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
